// bounded-widget-modal.ts - the shared in-app WIDGET shell for Bounded's login
// and signing surfaces. One Shadow-DOM overlay + panel that matches the Bounded
// widget (Seal) look and MOTION: a backdrop fade, a panel rise on desktop, a
// bottom-sheet on mobile, a height tween as content swaps, and a clean close.
//
// It hosts EITHER:
//   - NATIVE content (the login card the SDK renders itself), or
//   - an IFRAME (the Bounded-owned signer page, which must run on the single
//     signer origin because the wallet's 24h signing session lives in that
//     origin's storage, out of every app's reach).
//
// The modal is a warm singleton: it stays mounted (hidden) between uses so a
// second open - or a signature right after login - is instant and, when it is
// already open, a clean in-place view swap rather than a reopen.
function hasDOM() {
    return typeof window !== "undefined" && typeof document !== "undefined";
}
const OVERLAY_CSS = `
:host{all:initial}
*{box-sizing:border-box}
.bw-ov{position:fixed;inset:0;z-index:2147483647;display:none;
  font-family:"Schibsted Grotesk","Helvetica Neue",Arial,sans-serif}
.bw-ov.mounted{display:block}
.bw-back{position:fixed;inset:0;background:rgba(4,4,5,.62);opacity:0;
  transition:opacity .24s ease;-webkit-backdrop-filter:blur(5px);backdrop-filter:blur(5px)}
.bw-wrap{position:fixed;inset:0;display:flex;align-items:center;justify-content:center;padding:20px}
.bw-panel{position:relative;width:420px;max-width:calc(100vw - 28px);height:280px;
  border-radius:14px;overflow:hidden;background:#0e0f11;border:1px solid rgba(232,230,222,.08);
  box-shadow:0 1px 0 rgba(232,230,222,.03) inset,0 40px 90px -45px rgba(0,0,0,.85);
  opacity:0;transform:translateY(14px) scale(.98);will-change:transform,opacity,height;
  transition:opacity .28s cubic-bezier(.2,.8,.2,1),transform .28s cubic-bezier(.2,.8,.2,1),
    height .3s cubic-bezier(.2,.8,.2,1)}
.bw-ov.show .bw-back{opacity:1}
.bw-ov.show .bw-panel{opacity:1;transform:none}
.bw-ov.closing .bw-back{opacity:0}
.bw-ov.closing .bw-panel{opacity:0;transform:translateY(12px) scale(.99)}
.bw-content{position:absolute;inset:0;overflow:hidden}
.bw-frame{display:block;width:100%;height:100%;border:0;background:transparent;color-scheme:dark}
/* editorial corner crop-marks, matching the reference frame */
.bw-mk{position:absolute;width:13px;height:13px;pointer-events:none;z-index:2;
  border-color:rgba(232,230,222,.22);border-style:solid;border-width:0}
.bw-mk.tl{top:11px;left:11px;border-top-width:1px;border-left-width:1px}
.bw-mk.tr{top:11px;right:11px;border-top-width:1px;border-right-width:1px}
.bw-mk.bl{bottom:11px;left:11px;border-bottom-width:1px;border-left-width:1px}
.bw-mk.br{bottom:11px;right:11px;border-bottom-width:1px;border-right-width:1px}
@media(max-width:560px){
  .bw-wrap{align-items:flex-end;padding:0}
  .bw-panel{width:100%;max-width:100%;border-radius:18px 18px 0 0;border-bottom:0}
  .bw-content{overflow-y:auto}
  .bw-ov.show .bw-panel{transform:none}
  .bw-ov:not(.show) .bw-panel,.bw-ov.closing .bw-panel{transform:translateY(18%)}
}
@media(prefers-reduced-motion:reduce){.bw-back,.bw-panel{transition:opacity .12s linear!important}}
`;
let modal = null;
export function getModal() {
    if (modal)
        return modal;
    const root = document.createElement("div");
    root.setAttribute("data-bounded-widget", "");
    const shadow = root.attachShadow({ mode: "open" });
    const style = document.createElement("style");
    style.textContent = OVERLAY_CSS;
    shadow.appendChild(style);
    const overlay = document.createElement("div");
    overlay.className = "bw-ov";
    const back = document.createElement("div");
    back.className = "bw-back";
    const wrap = document.createElement("div");
    wrap.className = "bw-wrap";
    const panel = document.createElement("div");
    panel.className = "bw-panel";
    const content = document.createElement("div");
    content.className = "bw-content";
    panel.appendChild(content);
    for (const corner of ["tl", "tr", "bl", "br"]) {
        const mk = document.createElement("span");
        mk.className = "bw-mk " + corner;
        panel.appendChild(mk);
    }
    wrap.appendChild(panel);
    overlay.appendChild(back);
    overlay.appendChild(wrap);
    shadow.appendChild(overlay);
    (document.body || document.documentElement).appendChild(root);
    const m = {
        root,
        overlay,
        panel,
        content,
        iframe: null,
        iframeOrigin: "",
        iframeReady: false,
        onBackdrop: null,
        onIframeMessage: null,
        chain: Promise.resolve(),
        fitObserver: null,
        generation: 0,
        onSupersede: null,
    };
    // The dismiss handler lives on `.bw-wrap`: it is the full-viewport flex layer
    // that sits ABOVE `.bw-back`, so it (not the backdrop element) receives clicks
    // outside the panel. Fire only when the click lands on the wrap itself, never
    // when it bubbles up from the panel or its content.
    wrap.addEventListener("click", (event) => {
        if (event.target === wrap && m.onBackdrop)
            m.onBackdrop();
    });
    window.addEventListener("message", (event) => {
        if (!m.iframe)
            return;
        if (event.origin !== m.iframeOrigin)
            return;
        if (event.source !== m.iframe.contentWindow)
            return;
        const d = event.data;
        if (!d || typeof d !== "object")
            return;
        if (m.onIframeMessage)
            m.onIframeMessage(d);
    });
    modal = m;
    return m;
}
export function modalIsMounted() {
    return !!modal && modal.overlay.classList.contains("mounted");
}
export function showModal(m) {
    m.overlay.classList.remove("closing");
    m.overlay.classList.add("mounted");
    // Force a reflow so the .show transition runs from the hidden state every open.
    void m.panel.offsetWidth;
    m.overlay.classList.add("show");
}
export function hideModal(m, after) {
    m.overlay.classList.remove("show");
    m.overlay.classList.add("closing");
    // Capture the generation at hide time. If a newer open re-shows the modal within
    // the close tween, this stale timer must NOT strip `.mounted`/`.closing` off the
    // freshly reopened widget - only tear down when we are still the same open.
    const gen = m.generation;
    window.setTimeout(() => {
        if (m.generation === gen)
            m.overlay.classList.remove("mounted", "closing");
        if (after)
            after();
    }, 260);
}
export function setPanelHeight(m, px) {
    const h = Math.max(140, Math.min(680, Math.round(px)));
    m.panel.style.height = h + "px";
}
// True outer height of the mounted card: the max of its padding-box scroll height
// (catches overflowing descendants) and its border-box height plus own vertical
// margins (catches border + margins, which scrollHeight omits). Ceiled so a
// sub-pixel row is never clipped under `overflow:hidden`.
function measureOuterHeight(target) {
    var _a;
    const cs = (_a = target.ownerDocument.defaultView) === null || _a === void 0 ? void 0 : _a.getComputedStyle(target);
    const marginY = cs ? parseFloat(cs.marginTop) + parseFloat(cs.marginBottom) : 0;
    const borderBox = target.offsetHeight + (Number.isFinite(marginY) ? marginY : 0);
    const rect = target.getBoundingClientRect().height;
    return Math.ceil(Math.max(target.scrollHeight, borderBox, rect));
}
/**
 * Measure the currently mounted native content and tween the panel to fit it.
 * Re-fits automatically when the card's intrinsic height changes - notably after
 * the Newsreader/Schibsted web fonts swap in (which grows line boxes a few px
 * after first paint) and on inline view swaps (email -> OTP code -> success).
 */
export function fitPanelToContent(m) {
    var _a, _b;
    const first = m.content.firstElementChild;
    const target = first || m.content;
    const apply = () => {
        // Only fit while this exact node is still the mounted card.
        if (m.content.firstElementChild !== first && first)
            return;
        const h = measureOuterHeight(target);
        if (h > 0)
            setPanelHeight(m, h);
    };
    apply();
    const view = target.ownerDocument.defaultView;
    // Re-fit after fonts load (one-shot) and once the ResizeObserver-less browsers
    // have painted, so the height never lags a late reflow.
    (_a = view === null || view === void 0 ? void 0 : view.requestAnimationFrame) === null || _a === void 0 ? void 0 : _a.call(view, apply);
    const fonts = target.ownerDocument.fonts;
    if (fonts === null || fonts === void 0 ? void 0 : fonts.ready)
        fonts.ready.then(apply).catch(() => { });
    (_b = m.fitObserver) === null || _b === void 0 ? void 0 : _b.disconnect();
    m.fitObserver = null;
    if (first && typeof (view === null || view === void 0 ? void 0 : view.ResizeObserver) === "function") {
        const ro = new view.ResizeObserver(() => apply());
        ro.observe(first);
        m.fitObserver = ro;
    }
}
/** Switch the modal into NATIVE-content mode: detach any iframe, mount `node`. */
export function mountContent(m, node) {
    var _a;
    detachIframe(m);
    (_a = m.fitObserver) === null || _a === void 0 ? void 0 : _a.disconnect();
    m.fitObserver = null;
    m.content.replaceChildren(node);
    m.content.style.display = "block";
}
/** Get (or lazily create) the warm iframe for signer/hosted-page mode. */
export function ensureIframe(m, url, origin) {
    var _a;
    if (m.iframe && m.iframeOrigin === origin && m.iframe.src === url)
        return m.iframe;
    detachIframe(m);
    (_a = m.fitObserver) === null || _a === void 0 ? void 0 : _a.disconnect();
    m.fitObserver = null;
    m.content.style.display = "none";
    const iframe = document.createElement("iframe");
    iframe.className = "bw-frame";
    iframe.setAttribute("title", "Bounded");
    iframe.setAttribute("allow", "clipboard-write");
    iframe.src = url;
    m.iframeOrigin = origin;
    m.iframeReady = false;
    m.panel.appendChild(iframe);
    m.iframe = iframe;
    return iframe;
}
/**
 * Destroy the warm signer iframe if one is currently mounted for `origin`, and report
 * whether there was one. Logout needs this: a signing session can live in that document's
 * MEMORY (the signer keeps an in-memory mirror when the browser blocks its storage), and a
 * throwaway iframe opened by the sweep can only ever clear the shared store, never another
 * document's memory. Removing the iframe discards its browsing context, which takes the
 * mirror with it.
 *
 * Deliberately does NOT call getModal(): that would BUILD a modal just to tear it down, on
 * every logout of every app, most of which never mounted one.
 */
export function detachWarmSignerIframe(origin) {
    if (!modal || !modal.iframe || modal.iframeOrigin !== origin)
        return false;
    detachIframe(modal);
    return true;
}
export function detachIframe(m) {
    if (m.iframe) {
        try {
            m.iframe.remove();
        }
        catch (_a) {
            /* ignore */
        }
    }
    m.iframe = null;
    m.iframeReady = false;
    m.iframeOrigin = "";
    m.onIframeMessage = null;
}
// Load the Bounded editorial typefaces (Newsreader display + Schibsted Grotesk body +
// JetBrains Mono) once at the document level so they apply inside the shadow root. A
// no-op if already present or off-DOM. Fonts loaded here match the hosted auth page.
const FONTS_HREF = "https://fonts.googleapis.com/css2?family=Newsreader:opsz,wght@6..72,400;6..72,500&family=Schibsted+Grotesk:wght@400;500;600&family=JetBrains+Mono:wght@400;500&display=swap";
export function ensureFonts() {
    if (!hasDOM())
        return;
    if (document.querySelector('link[data-bounded-fonts="1"]'))
        return;
    const pre1 = document.createElement("link");
    pre1.rel = "preconnect";
    pre1.href = "https://fonts.googleapis.com";
    const pre2 = document.createElement("link");
    pre2.rel = "preconnect";
    pre2.href = "https://fonts.gstatic.com";
    pre2.crossOrigin = "anonymous";
    const link = document.createElement("link");
    link.rel = "stylesheet";
    link.href = FONTS_HREF;
    link.setAttribute("data-bounded-fonts", "1");
    document.head.append(pre1, pre2, link);
}
export { hasDOM };
